import { useEffect, useMemo, useState, type ChangeEvent } from "react";

export const hasInputValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return String(value).length > 0;
};

/**
 * Shared controlled/uncontrolled value handling for Ant Design Input wrappers.
 * Keeps `has-value` class logic consistent across text, password, search, and textarea inputs.
 */
export const useControlledInputValue = <
  E extends HTMLInputElement | HTMLTextAreaElement = HTMLInputElement,
>(
  value: unknown,
  defaultValue: unknown,
  onChange?: (_event: ChangeEvent<E>) => void
) => {
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(
    defaultValue?.toString() ?? ""
  );

  useEffect(() => {
    if (!isControlled) {
      setUncontrolledValue(defaultValue?.toString() ?? "");
    }
  }, [defaultValue, isControlled]);

  const currentValue = isControlled ? value : uncontrolledValue;
  const hasValue = useMemo(() => hasInputValue(currentValue), [currentValue]);

  const handleChange = (event: ChangeEvent<E>) => {
    if (!isControlled) {
      setUncontrolledValue(event.target.value);
    }
    onChange?.(event);
  };

  return {
    value: (isControlled ? value : uncontrolledValue) as
      | string
      | number
      | readonly string[]
      | undefined,
    hasValue,
    onChange: handleChange,
  };
};
